/**
 * @fileoverview AR-924 — "{Student}'s submission" line with the status badge, plus the "Uploaded by …" banner.
 */

import { Badge, Group, Paper, Text } from '@mantine/core'
import { IconUser } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import type { TaskTrackActor, TaskTrackDeliveryStatus } from '../../types'
import { formatLongDate } from './dateFormat'

const STATUS_COLOR: Record<TaskTrackDeliveryStatus, string> = {
  not_submitted: 'orange',
  on_time: 'green',
  late: 'red',
}

const BANNER_ICON_SIZE = 18

interface SubmissionHeaderProps {
  readonly studentFirstName: string
  readonly status: TaskTrackDeliveryStatus
  /** True when the status comes from the teacher's work-completed date. */
  readonly explainStatus: boolean
}

export function SubmissionHeader({ studentFirstName, status, explainStatus }: SubmissionHeaderProps) {
  const { t } = useTranslation()
  const tr = (key: string, options?: object) => t(`task_tracker.teacher_upload.${key}`, options)
  return (
    <Group spacing="sm" align="center">
      <Text size="md" weight={700}>{tr('submission_title', { name: studentFirstName })}</Text>
      <Badge variant="light" color={STATUS_COLOR[status]} radius="sm" size="md" sx={{ fontWeight: 700 }}>
        {tr(status)}
      </Badge>
      {explainStatus && <Text size="xs" color="dimmed">{tr('from_work_completed_date')}</Text>}
    </Group>
  )
}

interface UploadedByBannerProps {
  readonly uploadedBy: TaskTrackActor
  readonly studentFirstName: string
  readonly submittedAt: string
}

export function UploadedByBanner({ uploadedBy, studentFirstName, submittedAt }: UploadedByBannerProps) {
  const { t, i18n } = useTranslation()
  const date = formatLongDate(submittedAt, i18n.language)
  const text = uploadedBy.currentUser
    ? t('task_tracker.teacher_upload.uploaded_by_you_on_behalf', { name: studentFirstName, date })
    : t('task_tracker.teacher_upload.uploaded_by_on_behalf', { teacher: uploadedBy.name, name: studentFirstName, date })
  return (
    <Paper
      radius="md"
      px="md"
      py="sm"
      sx={theme => ({ background: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).background })}
    >
      <Group spacing="xs" noWrap align="center" sx={theme => ({ color: theme.fn.primaryColor() })}>
        <IconUser size={BANNER_ICON_SIZE} style={{ flexShrink: 0 }} />
        <Text size="sm" weight={500} color="inherit">{text}</Text>
      </Group>
    </Paper>
  )
}
